import * as Sentry from '@sentry/react';

/**
 * Transport / client failures where reporting adds quota noise and no engineering action is expected.
 */
function isBenignFetchFailure(error) {
  if (!error) return true;
  if (error.name === 'AbortError') return true;
  const msg = String(error.message || '').toLowerCase();
  if (error instanceof TypeError) {
    if (
      msg.includes('load failed') ||
      msg.includes('failed to fetch') ||
      msg.includes('networkerror when attempting to fetch')
    ) {
      return true;
    }
  }
  if (error.name === 'NetworkError' && msg.includes('fetch')) return true;
  return false;
}

/**
 * Wrapper class that can be used to make a request to the commerce API.
 * @param {string} endpoint - The API endpoint to call.
 * @param {Object} [options={}] - The options for the fetch request.
 * @param {string} [options.method='GET'] - The HTTP method to use.
 * @param {Object} [options.headers] - Additional headers to include in the request.
 * @param {string} [options.body] - The body of the request for POST/PUT methods.
 * @returns {Promise<Object|string|null>} The response data, text, or null on failure.
 *
 * @example
 * // To make a GET request:
 * CommerceApiFetchWrapper('/endpoint', { method: 'GET' });
 *
 * @example
 * // To make a POST request:
 * CommerceApiFetchWrapper('/endpoint', { method: 'POST', body: JSON.stringify(data) });
 */
async function CommerceApiFetchWrapper(endpoint, options = {}) {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    Locale: getLocale(),
    Brand: getBrand(),
    NShiftEnabled: getNShiftEnabled(),
  };

  const fetchOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
    credentials: 'include',
  };

  const commerceApiBaseUrl = window._THULEDATA._siteHeader?.CommerceApiExternalUrl;
  const fullUrl = `${commerceApiBaseUrl}${endpoint}`;

  const method = fetchOptions.method || 'GET';

  try {
    const response = await fetch(fullUrl, fetchOptions);
    if (!response.ok) {
      Sentry.addBreadcrumb({
        category: 'commerce-api',
        level: response.status >= 500 ? 'error' : 'warning',
        message: `${method} ${endpoint} ${response.status}`,
        data: { endpoint, method, status: response.status },
      });
      return null;
    }
    //Handle 204 No Content or empty body — .json() throws on empty string
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (parseError) {
      // Non-empty but non-JSON response (e.g. HTML error page leaking through).
      // Keep reporting these — they indicate a real server-side problem.
      Sentry.captureException(parseError, {
        tags: { source: 'CommerceApiFetchWrapper' },
        extra: { endpoint, fullUrl, responsePreview: text.slice(0, 200) },
      });
      return null;
    }
  } catch (error) {
    if (isBenignFetchFailure(error)) {
      Sentry.addBreadcrumb({
        category: 'commerce-api',
        level: 'warning',
        message: `${method} ${endpoint} failed: ${error?.name}`,
        data: { endpoint, method, error: error?.message },
      });
      return null;
    }
    Sentry.captureException(error, {
      tags: { source: 'CommerceApiFetchWrapper' },
      extra: { endpoint, fullUrl },
    });
    return null;
  }
}

function getLocale() {
  const locale = window._THULEDATA?.SitecoreLanguage;
  return locale;
}

function getBrand() {
  const brand = window._THULEDATA?.SitecoreSiteName;
  return brand === 'CaseLogic' ? 'Case Logic' : brand; //CT has a space in the brand name, while Sitecore has without...
}

function getNShiftEnabled() {
  const nShiftEnabled = window._THULEDATA?._Cart?.enableNShiftCheckout;
  return nShiftEnabled;
}

export default CommerceApiFetchWrapper;
